import type { AppConfig } from '@/types/config';

/**
 * Helpers for PLP/search product grids that split SCAPI results into critical hits (SSR)
 * and a deferred non-critical batch. These functions only size skeleton placeholders —
 * they do not fetch data.
 */

/** Fallback when `search.products.hits.skeletons.desktop` is unset (≈2 rows × 3 cols at lg). */
export const DEFAULT_NON_CRITICAL_SKELETONS_DESKTOP = 6;
/** Fallback when `search.products.hits.skeletons.mobile` is unset (≈3 rows × 1 col). */
export const DEFAULT_NON_CRITICAL_SKELETONS_MOBILE = 3;
/** Fallback when `search.products.hits.skeletons.eventDesktop` is unset (≈2 rows × 4 cols at lg). */
export const DEFAULT_EVENT_NON_CRITICAL_SKELETONS_DESKTOP = 8;
/** Fallback when `search.products.hits.skeletons.eventMobile` is unset (≈2 rows × 2 cols). */
export const DEFAULT_EVENT_NON_CRITICAL_SKELETONS_MOBILE = 4;

type ProductSearchHitsConfig = AppConfig['search']['products']['hits'];

/**
 * Reads the configured skeleton cap for the current viewport.
 * Grocery uses `skeletons.desktop` / `skeletons.mobile`; event/course listings use
 * `eventDesktop` / `eventMobile`. Pair with `useMediaQuery('(min-width: 1024px)')`.
 */
export function getMaxNonCriticalSkeletonCount(
    hits: ProductSearchHitsConfig,
    isDesktop: boolean,
    isEventListing = false
): number {
    const { skeletons } = hits;
    if (isEventListing) {
        return isDesktop
            ? (skeletons?.eventDesktop ?? DEFAULT_EVENT_NON_CRITICAL_SKELETONS_DESKTOP)
            : (skeletons?.eventMobile ?? DEFAULT_EVENT_NON_CRITICAL_SKELETONS_MOBILE);
    }
    return isDesktop
        ? (skeletons?.desktop ?? DEFAULT_NON_CRITICAL_SKELETONS_DESKTOP)
        : (skeletons?.mobile ?? DEFAULT_NON_CRITICAL_SKELETONS_MOBILE);
}

/**
 * How many skeleton tiles to render while the non-critical promise is pending.
 * Capped by `maxSkeletons`, remaining page slots (`limit - criticalCount`), and hits
 * still available (`total - offset - criticalCount`). Never negative.
 *
 * When `total` equals the critical batch (e.g. one product in the category), this returns 0 —
 * use {@link getMaxNonCriticalSkeletonCount} via `ProductGrid` `maxSkeletonCount` during refine loading instead.
 */
export function getNonCriticalSkeletonCount({
    maxSkeletons,
    limit,
    criticalCount,
    total,
    offset,
}: {
    maxSkeletons: number;
    limit: number;
    criticalCount: number;
    total: number;
    offset: number;
}): number {
    return Math.max(0, Math.min(maxSkeletons, limit - criticalCount, total - offset - criticalCount));
}
